'use client';

import { useLanguage } from '@bells/core/i18n';
import {
  attendantAboutTitle,
  attendantsAge,
  attendantSedCardActivities,
  attendantSedCardAge,
  attendantSedCardBreastScope,
  attendantSedCardBreastSilicon,
  attendantSedCardBreastSiliconNo,
  attendantSedCardBreastSiliconYes,
  attendantSedCardBreastWeight,
  attendantSedCardClothesSize,
  attendantSedCardDrinker,
  attendantSedCardDrinkPrefs,
  attendantSedCardFeedSize,
  attendantSedCardFitnessLevel,
  attendantSedCardFootPrefs,
  attendantSedCardGifts,
  attendantSedCardHeight,
  attendantSedCardIntimaZone,
  attendantSedCardJob,
  attendantSedCardLanguages,
  attendantSedCardMobilityStatus,
  attendantSedCardOrigin,
  attendantSedCardSexualOrientation,
  attendantSedCardSmoker,
  attendantSedCardWeight,
} from '@bells/i18n';
import {
  AttendantSearchSingleResponse,
  convertEUClothesSize,
} from '@bells/schema/models/attendant';
import { Badge } from '@bells/ui/components/badge';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@bells/ui/components/card';
import {
  Activities,
  DrinkPreferences,
  FitnessLevels,
  FoodPreferences,
  Gifts,
  IntimaZones,
  JobPositions,
  MobilityStatuses,
  Piercings,
  SexualOrientations,
  SmokerStatuses,
  Tattoos,
} from '@bells/types/attendant';
import { useMemo } from 'react';
import countries from '@bells/iso/countries';
import languages from '@bells/iso/languages';
import { compact } from 'lodash';
import { Rating } from '@bells/ui/components/rating';
import { CircleFlagLanguage } from 'react-circle-flags';
import { HairColors } from '@bells/ui/shared/attendant-types';
import { FlagIcon } from '@bells/ui/components/flag-icon';

export const GirlAbout = ({
  data,
}: {
  data: AttendantSearchSingleResponse;
}) => {
  const { language } = useLanguage();

  const intimaZones = useMemo(() => {
    return IntimaZones(language);
  }, [language]);

  const tattoos = useMemo(() => {
    return Tattoos(language);
  }, [language]);

  const piercings = useMemo(() => {
    return Piercings(language);
  }, [language]);

  const foodPreferences = useMemo(() => {
    return FoodPreferences(language);
  }, [language]);

  const drinkPreferences = useMemo(() => {
    return DrinkPreferences(language);
  }, [language]);

  const activities = useMemo(() => {
    return Activities(language);
  }, [language]);

  const gifts = useMemo(() => {
    return Gifts(language);
  }, [language]);

  const mobilityStatuses = useMemo(() => {
    return MobilityStatuses(language);
  }, [language]);

  const smokers = useMemo(() => {
    return SmokerStatuses(language);
  }, [language]);

  const jobs = useMemo(() => {
    return JobPositions(language);
  }, [language]);

  const fitnessLevels = useMemo(() => {
    return FitnessLevels(language);
  }, [language]);

  const sexualOrientations = useMemo(() => {
    return SexualOrientations(language);
  }, [language]);

  const hairColor = useMemo(() => {
    if (data.hairColor) {
      const colors = HairColors(language);
      return colors.find((val) => val.value == data.hairColor);
    } else {
      return undefined;
    }
  }, [language, data.hairColor]);

  return (
    <Card className='w-full border-0 bg-transparent shadow-none lg:border lg:shadow-sm'>
      <CardHeader className='mt-0 px-0 pt-0 lg:p-6'>
        <CardTitle>{attendantAboutTitle()}</CardTitle>
      </CardHeader>
      <CardContent className='flex w-full flex-col gap-6 space-y-4 px-0 lg:p-6'>
        {data.biopic && <p className='font-sans'>{data.biopic}</p>}

        <div className='grid grid-cols-2 rounded-xl border lg:grid-cols-4 [&>*:nth-child(2n)]:border-r-0 lg:[&>*:nth-child(2n)]:border-r lg:[&>*:nth-child(4n)]:border-r-0 [&>*:nth-last-child(-n+2)]:border-b-0 lg:[&>*:nth-last-child(-n+4)]:border-b-0 [&>*]:border-b [&>*]:border-r [&>*]:p-4'>
          {data.age != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                {attendantSedCardAge()}
              </span>
              <span className='font-medium'>
                {attendantsAge({ age: data.age ?? '' })}
              </span>
            </div>
          )}
          {data.height != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                {attendantSedCardHeight()}
              </span>
              <span className='font-medium'>{data.height} cm</span>
            </div>
          )}
          {data.weight != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                {attendantSedCardWeight()}
              </span>
              <span className='font-medium'>{data.weight} kg</span>
            </div>
          )}

          {(data.breastWeight || data.breastScope) && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                {attendantSedCardBreastScope()}
              </span>
              <span className='font-medium'>
                {compact([data.breastWeight, data.breastScope]).join(' ')}
              </span>
            </div>
          )}
          {data.breastSilicon != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                {attendantSedCardBreastSilicon()}
              </span>
              <span className='font-medium'>
                {data.breastSilicon
                  ? attendantSedCardBreastSiliconYes()
                  : attendantSedCardBreastSiliconNo()}
              </span>
            </div>
          )}
          {data.dressSize != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                {attendantSedCardClothesSize()}
              </span>
              <span className='font-medium'>
                {data.dressSize} EU ({convertEUClothesSize(data.dressSize)})
              </span>
            </div>
          )}
          {data.intimaZone != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                {attendantSedCardIntimaZone()}
              </span>
              <span className='font-medium'>
                {intimaZones[data.intimaZone]}
              </span>
            </div>
          )}
          {data.footSize != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                {attendantSedCardFeedSize()}
              </span>
              <span className='font-medium'>{data.footSize} EU</span>
            </div>
          )}
          {data.tattoos != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                Tattoos
              </span>
              <span className='font-medium'>{tattoos[data.tattoos]}</span>
            </div>
          )}
          {hairColor != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                Hair hairColor
              </span>
              <span className='inline-flex items-center gap-2 font-medium'>
                {hairColor.icon}
                {hairColor.label}
              </span>
            </div>
          )}
          {data.smoker != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                {attendantSedCardSmoker()}
              </span>
              <span className='font-medium'>{smokers[data.smoker]}</span>
            </div>
          )}
          {data.drinker != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                {attendantSedCardDrinker()}
              </span>
              <span className='font-medium'>
                {data.drinker
                  ? attendantSedCardBreastSiliconYes()
                  : attendantSedCardBreastSiliconNo()}
              </span>
            </div>
          )}

          {data.shownOrigin != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                {attendantSedCardOrigin()}
              </span>
              <span className='inline-flex items-center gap-1 font-medium'>
                <FlagIcon
                  countryCode={data.shownOrigin.toUpperCase()}
                  className='h-4 w-4'
                ></FlagIcon>
                {countries[data.shownOrigin].names[language]}
              </span>
            </div>
          )}
          {data.jobPosition != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                {attendantSedCardJob()}
              </span>
              <span className='font-medium'>{jobs[data.jobPosition]}</span>
            </div>
          )}

          {data.fitnessLevel != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                {attendantSedCardFitnessLevel()}
              </span>
              <span className='font-medium'>
                {fitnessLevels[data.fitnessLevel]}
              </span>
            </div>
          )}
          {data.sexualOrientation != null && (
            <div className='flex flex-col gap-2'>
              <span className='text-xs uppercase text-muted-foreground'>
                {attendantSedCardSexualOrientation()}
              </span>
              <span className='font-medium'>
                {sexualOrientations[data.sexualOrientation]}
              </span>
            </div>
          )}
        </div>

        <div className='grid grid-cols-1 gap-8 lg:grid-cols-2'>
          {data.piercings != null && data.piercings.length > 0 && (
            <div className='flex flex-col gap-2'>
              <span className='text-base font-medium'>Piercings</span>
              <div className='inline-flex flex-wrap gap-2 font-medium'>
                {data.piercings.map((df) => {
                  return (
                    <Badge
                      className='break-all text-base font-normal text-muted-foreground'
                      variant={'outline'}
                      key={df}
                    >
                      {piercings[df]}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {data.foodPreferences != null && data.foodPreferences.length > 0 && (
            <div className='flex flex-col gap-2'>
              <span className='text-base font-medium'>
                {attendantSedCardFootPrefs()}
              </span>
              <div className='inline-flex flex-wrap gap-2 font-medium'>
                {data.foodPreferences.map((df) => {
                  return (
                    <Badge
                      className='break-all text-base font-normal text-muted-foreground'
                      variant={'outline'}
                      key={df}
                    >
                      {foodPreferences[df]}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {data.drinkPreferences != null &&
            data.drinkPreferences.length > 0 && (
              <div className='flex flex-col gap-2'>
                <span className='text-base font-medium'>
                  {attendantSedCardDrinkPrefs()}
                </span>
                <div className='inline-flex flex-wrap gap-2 font-medium'>
                  {data.drinkPreferences.map((df) => {
                    return (
                      <Badge
                        className='break-all text-base font-normal text-muted-foreground'
                        variant={'outline'}
                        key={df}
                      >
                        {drinkPreferences[df]}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

          {data.activities != null && data.activities.length > 0 && (
            <div className='flex flex-col gap-2'>
              <span className='text-base font-medium'>
                {attendantSedCardActivities()}
              </span>
              <div className='inline-flex flex-wrap gap-2 font-medium'>
                {data.activities.map((df) => {
                  return (
                    <Badge
                      className='break-all text-base font-normal text-muted-foreground'
                      variant={'outline'}
                      key={df}
                    >
                      {activities[df]}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
          {data.gift != null && data.gift.length > 0 && (
            <div className='flex flex-col gap-2'>
              <span className='text-base font-medium'>
                {attendantSedCardGifts()}
              </span>
              <div className='inline-flex flex-wrap gap-2 font-medium'>
                {data.gift.map((df) => {
                  return (
                    <Badge
                      className='break-all text-base font-normal text-muted-foreground'
                      variant={'outline'}
                      key={df}
                    >
                      {gifts[df]}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {data.languages != null && data.languages.length > 0 && (
            <div className='flex flex-col gap-2'>
              <span className='text-base font-medium'>
                {attendantSedCardLanguages()}
              </span>
              <div className='inline-flex flex-wrap gap-2 font-medium'>
                {data.languages.map((df) => {
                  return (
                    <Badge
                      className='inline-flex flex-wrap items-center gap-2 break-all text-base font-normal text-muted-foreground'
                      variant={'outline'}
                      key={df.language}
                    >
                      <CircleFlagLanguage
                        alt={languages[df.language].names[language]}
                        className='h-4 w-4'
                        loading='lazy'
                        languageCode={df.language}
                      ></CircleFlagLanguage>

                      <span>{languages[df.language].names[language]}</span>

                      <Rating
                        size={12}
                        variant='yellow'
                        disabled={true}
                        rating={df.rating}
                      ></Rating>
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {data.mobilityStatus != null && data.mobilityStatus.length > 0 && (
            <div className='flex flex-col gap-2'>
              <span className='text-base font-medium'>
                {attendantSedCardMobilityStatus()}
              </span>
              <div className='inline-flex flex-wrap gap-2 font-medium'>
                {data.mobilityStatus.map((df) => {
                  return (
                    <Badge
                      className='break-all text-base font-normal text-muted-foreground'
                      variant={'outline'}
                      key={df}
                    >
                      {mobilityStatuses[df]}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
